import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ObNotificationService} from '@oblique/oblique';

import {AdvancedCharts} from '../dtos/cockpit/advancedCharts/advanced-charts';
import {
  AdvancedChartsM2,
  Charts,
  ChartsM2
} from '../dtos/cockpit/charts/charts';
import {Cockpit, CockpitM2} from '../dtos/cockpit/cockpit';
import {DataInput} from '../dtos/data-input';
import {DataResult, DataResultM2} from '../dtos/data-result';
import {AnalyzerError} from '../dtos/error';
import {FileInfo} from '../dtos/fileInfo';
import {FunctionRating} from '../dtos/function-rating';
import {MitarbeiterBase} from '../dtos/mitarbeiterBase';
import {ComplianceRiskFunktionsebene} from '../dtos/result/result-m2/compliance-risk-funktionsebene';
import {ComplianceRiskVergleichspaare} from '../dtos/result/result-m2/compliance-risk-vergleichspaare';
import {AdditionalInfo} from '../dtos/wizard/additional-info';
import {EnterpriseInfo} from '../dtos/wizard/enterprise-info';
import {IndexedDbStorageService} from './storage/indexed-db-storage.service';
import {CockpitKontrolle} from '../dtos/cockpit-kontrolle/cockpit-kontrolle';
import {ResultDataMitarbeiter} from '../dtos/x-mitarbeiterBase';
import {ExportDetailErgebnis} from '../dtos/result/result-m2/export-detail-ergebnis';

const additionalInfoStorageKey = 'additionalInfo';
const funktionenStorageKey = 'funktionen';
const enterpriseInfoStorageKey = 'enterpriseInfo';
const fileInfoStorageKey = 'fileInfo';
const resultDataStorageKey = 'dataResult';
const cockpitStorageKey = 'cockpit';
const chartsStorageKey = 'charts';
const advancedChartsStorageKey = 'advancedCharts';
const analyzerErrorStorageKey = 'error';
const disclaimerReadStorageKey = 'disclaimerRead';
const fileResetStorageKey = 'fileReset';
const disabledAddInfoStorageKey = 'disabledAddInfo';
const hasUnsavedChangesStorageKey = 'hasUnsavedChanges';
const maxAnforderungsNiveauStorageKey = 'maxAnforderungsNiveau';
const lessThan50StorageKey = 'lessThan50';
const allowMultiUpdateBknBstStorageKey = 'allowMultiUpdateBknBst';
const isForModule2StorageKey = 'isForModule2';
const complianceRiskFunktionsebeneStorageKey = 'complianceRiskFunktionsebene';
const mitarbeitersTransformedStorageKey = 'mitarbeitersTransformed';
const kontrollmodusStorageKey = 'kontrollmodus';
const cockpitKontrolleStorageKey = 'cockpitKontrolle';
const prioritaetAlterStorageKey = 'prioritaetAlter';

@Injectable({
  providedIn: 'root'
})
export class DataStorageService {
  private readonly memoryStore: Record<string, unknown>;
  private mitarbeiters: MitarbeiterBase[] = [];

  constructor(
    private readonly notification: ObNotificationService,
    private readonly translate: TranslateService,
    private readonly idxDbStore: IndexedDbStorageService
  ) {
    this.memoryStore = {};
  }

  public async storeAdditionalInfo(ai: AdditionalInfo) {
    this.store(additionalInfoStorageKey, ai);
  }

  public async getAdditionalInfo(): Promise<AdditionalInfo> {
    return this.get(additionalInfoStorageKey);
  }

  public async storeFunktionen(ei: FunctionRating[]) {
    this.store(funktionenStorageKey, ei);
  }

  public async getFunktionen(): Promise<FunctionRating[]> {
    return this.get(funktionenStorageKey);
  }

  public async storeEnterpriseInfo(ei: EnterpriseInfo) {
    this.store(enterpriseInfoStorageKey, ei);
  }

  public async getEnterpriseInfo(): Promise<EnterpriseInfo> {
    return this.get(enterpriseInfoStorageKey);
  }

  public async storeFileInfo(fi: FileInfo) {
    this.store(fileInfoStorageKey, fi);
  }

  public async getFileInfo(): Promise<FileInfo> {
    return this.get(fileInfoStorageKey);
  }

  public async storeResultData(result: DataResult | DataResultM2) {
    this.store(resultDataStorageKey, result);
  }

  public async getResultData(): Promise<DataResult | DataResultM2> {
    return this.get(resultDataStorageKey);
  }

  public async storeCockpitData(cockpitData: Cockpit | CockpitM2) {
    this.store(cockpitStorageKey, cockpitData);
  }

  public async getCockpitData(): Promise<Cockpit | CockpitM2> {
    return this.get(cockpitStorageKey);
  }

  public async storeChartsData(chartsData: Charts | ChartsM2) {
    this.store(chartsStorageKey, chartsData);
  }

  public async getChartsData(): Promise<Charts | ChartsM2> {
    return this.get(chartsStorageKey);
  }

  public async storeAdvancedChartsData(
    chartsData: AdvancedCharts | AdvancedChartsM2
  ) {
    this.store(advancedChartsStorageKey, chartsData);
  }

  public async getAdvancedChartsData(): Promise<
    AdvancedCharts | AdvancedChartsM2
  > {
    return this.get(advancedChartsStorageKey);
  }

  public async storeAnalyzerError(error: AnalyzerError) {
    this.store(analyzerErrorStorageKey, error);
  }

  public async getAnalyzerError(): Promise<AnalyzerError> {
    return this.get(analyzerErrorStorageKey);
  }

  public async storeDisclaimerRead(read: boolean) {
    this.store(disclaimerReadStorageKey, read);
  }

  public async getPrioritaetAlter(): Promise<boolean> {
    return this.get(prioritaetAlterStorageKey);
  }

  public async storePrioritaetAlter(prio: boolean) {
    this.store(prioritaetAlterStorageKey, prio);
  }

  public async getDisclaimerRead(): Promise<boolean> {
    return this.get(disclaimerReadStorageKey);
  }

  public async storeFileReset(read: boolean) {
    this.store(fileResetStorageKey, read);
  }

  public async getFileReset(): Promise<boolean> {
    return this.get(fileResetStorageKey);
  }

  public async storeDisableAdditionalInfo(isDisabled: boolean) {
    this.store(disabledAddInfoStorageKey, isDisabled);
  }

  public async getDisableAdditionalInfo(): Promise<boolean> {
    return this.get(disabledAddInfoStorageKey);
  }

  public async storeMaxAnforderungsNiveau(maxAnfNiveau: number) {
    this.store(maxAnforderungsNiveauStorageKey, maxAnfNiveau);
  }

  public async getMaxAnforderungsNiveau(): Promise<number> {
    return this.get(maxAnforderungsNiveauStorageKey);
  }

  public async storeUnsavedChanges(hasUnsavedChanges: boolean) {
    this.store(hasUnsavedChangesStorageKey, hasUnsavedChanges);
  }

  public async getUnsavedChanges(): Promise<boolean> {
    return this.get(hasUnsavedChangesStorageKey);
  }

  public async storeLessThan50(lt50: boolean) {
    this.store(lessThan50StorageKey, lt50);
  }

  public async getLessThan50(): Promise<boolean> {
    return this.get(lessThan50StorageKey);
  }

  public async storeAllowMultiUpdateBknBst(isAllowMultiUpdateBknBst: boolean) {
    this.store(allowMultiUpdateBknBstStorageKey, isAllowMultiUpdateBknBst);
  }

  public async getAllowMultiUpdateBknBst(): Promise<boolean> {
    return this.get(allowMultiUpdateBknBstStorageKey);
  }

  public async storeIsForModule2(isForModule2: boolean) {
    this.store(isForModule2StorageKey, isForModule2);
  }

  public async getIsForModule2(): Promise<boolean | null> {
    return this.get(isForModule2StorageKey);
  }

  public async storeComplianceRiskVergleichspaare(
    data: ComplianceRiskVergleichspaare | null
  ): Promise<void> {
    return this.idxDbStore.storeAllVergleichspaare(data);
  }

  public async getComplianceRiskVergleichspaare(): Promise<ComplianceRiskVergleichspaare | null> {
    return this.idxDbStore.getAllVergleichspaare();
  }

  public async storeComplianceRiskFunktionsebene(
    data: ComplianceRiskFunktionsebene | null
  ): Promise<void> {
    return this.store(complianceRiskFunktionsebeneStorageKey, data);
  }

  public async getComplianceRiskFunktionsebene(): Promise<ComplianceRiskFunktionsebene | null> {
    return this.get(complianceRiskFunktionsebeneStorageKey);
  }

  public async storeKontrollmodus(kontrollmodus: boolean): Promise<void> {
    this.store(kontrollmodusStorageKey, kontrollmodus);
  }

  public async getKontrollmodus(): Promise<boolean> {
    return this.get(kontrollmodusStorageKey);
  }

  public async storeCockpitKontrolle(data: CockpitKontrolle) {
    return this.store(cockpitKontrolleStorageKey, data);
  }

  public async getCockpitKontrolle(): Promise<CockpitKontrolle> {
    return this.get(cockpitKontrolleStorageKey);
  }

  public async storeExportDetailergebnis(
    exportDetailErgebnis: ExportDetailErgebnis
  ) {
    return this.idxDbStore.storeExportDetailergebnis(exportDetailErgebnis);
  }

  public async getExportDetailergebnis(): Promise<ExportDetailErgebnis | null> {
    return this.idxDbStore.getExportDetailergebnis();
  }

  public async getAnalyzerRequestData(): Promise<DataInput> {
    const data = new DataInput();
    data.additionalInfo = await this.getAdditionalInfo();
    data.enterpriseInfo = await this.getEnterpriseInfo();
    data.fileInfo = await this.getFileInfo();
    data.mitarbeiter = await this.getMitarbeiter();
    if (this.memoryStore[isForModule2StorageKey]) {
      data.funktionen = await this.getFunktionen();
    }
    data.kontrollmodus = await this.getKontrollmodus();

    return data;
  }

  public async getExtendedDocumentDataM2(scen: number, rbScore: number) {
    return {
      additionalInfo: await this.getAdditionalInfo(),
      enterpriseInfo: await this.getEnterpriseInfo(),
      dataResult: await this.getResultData(),
      fileInfo: await this.getFileInfo(),
      cockpit: await this.getCockpitData(),
      complianceRiskFunktionsebene:
        await this.getComplianceRiskFunktionsebene(),
      funktionen: await this.getFunktionen(),
      scenario: scen,
      robustscore: rbScore
    };
  }

  public async getExtendedDocumentDataM1(scen: number) {
    return {
      additionalInfo: await this.getAdditionalInfo(),
      enterpriseInfo: await this.getEnterpriseInfo(),
      dataResult: (await this.getResultData()) as DataResult,
      fileInfo: await this.getFileInfo(),
      cockpit: (await this.getCockpitData()) as Cockpit,
      funktionen: await this.getFunktionen(),
      scenario: scen
    };
  }

  public async storeMitarbeiter(
    mitarbeiters: MitarbeiterBase[],
    isTransformed = this.memoryStore[
      mitarbeitersTransformedStorageKey
    ] as boolean,
    displayWarningIfInaccessible = false
  ): Promise<void> {
    this.mitarbeiters = mitarbeiters;
    this.memoryStore[mitarbeitersTransformedStorageKey] = isTransformed;
    try {
      await this.idxDbStore.storeMitarbeiter(
        {mitarbeiters, isTransformed},
        displayWarningIfInaccessible
      );
    } catch (e: unknown) {
      console.warn('Error storing mitarbeiter:', e);
    }
    return Promise.resolve();
  }

  public async getMitarbeiter(): Promise<MitarbeiterBase[]> {
    if (!this.mitarbeiters || this.mitarbeiters.length <= 0) {
      try {
        const data = await this.idxDbStore.getMitarbeiter();
        if (data) {
          this.memoryStore[mitarbeitersTransformedStorageKey] =
            data.isTransformed;
          this.mitarbeiters = data.mitarbeiters;
        }
      } catch (e: unknown) {
        console.warn('Could not get Mitarbeiters from IndexedDB:', e);
      }
    }

    return this.mitarbeiters ?? [];
  }

  public async getMitarbeiterCount(): Promise<number> {
    const mas = await this.getMitarbeiter();
    return mas ? mas.length : 0;
  }

  public async mitarbeitersAreTransformed(): Promise<boolean> {
    await this.getMitarbeiter();
    return this.memoryStore[mitarbeitersTransformedStorageKey] as boolean;
  }

  public async getDebug(): Promise<boolean> {
    return this.retrieveFromCachedStorage<boolean>(sessionStorage, 'debug');
  }

  public async storeKontrollmodusMitarbeiter(
    data: ResultDataMitarbeiter[] | null
  ): Promise<void> {
    return this.idxDbStore.storeKontrollmodusMitarbeiter(data);
  }

  public async getKontrollmodusMitarbeiter(): Promise<
    ResultDataMitarbeiter[] | null
  > {
    return this.idxDbStore.getKontrollmodusMitarbeiter();
  }

  /**
   * Stores any data in the Browser's storage while keeping a cache copy in memory.
   * @param key A unique key to identify the data to store by.
   * @param data The data to store at the specified key.
   * @param storage The storage the data is written to. If null, the data is cached in memory only.
   */
  public store<T>(key: NonNullable<string>, data: T, storage = sessionStorage) {
    this.persistToCachedStorage(storage, key, data);
  }

  /**
   * Gets any data stored in the Browser's storage or memory cache.
   * @param key The unique key for the stored data to retreive.
   * @param storage The storage from which the data is retreived. If null, the data is retreived only from memory.
   */
  public get<T>(key: NonNullable<string>, storage = sessionStorage): T | null {
    return this.retrieveFromCachedStorage<T>(storage, key);
  }

  private persistToCachedStorage<T>(
    storage: Storage | null,
    key: NonNullable<string>,
    item: T | null
  ) {
    this.memoryStore[key] = item;
    if (storage) {
      try {
        storage.setItem(key, JSON.stringify(item));
      } catch (e: unknown) {
        this.notification.warning({
          message: this.translate.instant(
            'm2.sprint4.common.dataStorage.quotaExceeded'
          ),
          timeout: 5000,
          sticky: true
        });
        console.error('Error when persisting ', key, ' in Storage: ', e);
      }
    }
  }

  private retrieveFromCachedStorage<T>(
    storage: Storage | null,
    key: string
  ): T | null {
    try {
      if (this.memoryStore[key]) {
        return this.memoryStore[key] as T;
      }
      if (storage) {
        const dataString = storage.getItem(key);
        if (dataString) {
          return JSON.parse(dataString);
        }
      }
    } catch (e: unknown) {
      console.warn(e);
    }
    return null;
  }
}
